import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaHome, FaChevronRight, FaHeadset, FaMapMarkerAlt,
  FaSearch, FaDirections, FaTimes
} from 'react-icons/fa';
import { database, ref as dbRef, get } from '../../firebase';
import { useLanguage } from '../../contexts/LanguageContext';

const CustomerServiceCenter = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  // (language comes from context; no local selectedLanguage needed)

  // Translations for customer service center page
  const translations = {
    en: {
      pageTitle: 'Customer Service Center',
      pageSubtitle: 'Reliable Service, Convenient Locations',
      breadcrumbHome: 'Home',
      totalCenters: 'Total Centers',
      districts: 'Districts',
      customerSupport: 'Customer Support',
      searchPlaceholder: 'Search customer service centers...',
      ourCenters: 'Our Customer Service Centers',
      ourCentersDesc: 'Our customer service centers at convenient locations across Maharashtra',
      totalCentersInfo: '{count} customer service centers in Maharashtra',
      noResults: 'No service centers found',
      noResultsDesc: 'Please change your search',
      showAllCenters: 'Show All Centers',
      moreInfo: 'More Info',
      directions: 'Google Map',
      contactInfo: 'Contact Information',
      address: 'Address',
      details: 'Details',
      location: 'Location',
      quickActions: 'Quick Actions',
      getDirections: 'Get Directions',
      additionalHelp: 'Additional Help',
      additionalHelpDesc: 'Contact us if you need more assistance',
      customerService: 'Customer Service',
      customerServiceDesc: 'Visit our service centers',
      serviceCenters: 'Service Centers',
      locationsInMaharashtra: 'Various locations in Maharashtra',
      visitNearbyCenter: 'Visit Nearby',
      customerServiceCenter: 'Customer Service Center',
      findNearbyCenter: 'Contact Us',
      personalizedAssistance: 'Get personalized assistance at our {count} convenient customer service center locations',
      thisCenterDedicated: 'This service center is dedicated to providing various customer services.',
      responseTime: 'Within 24 hrs'
    },
    mr: {
      pageTitle: 'ग्राहक सेवा केंद्र',
      pageSubtitle: 'विश्वासार्ह सेवा, सोयीस्कर स्थान',
      breadcrumbHome: 'होम',
      totalCenters: 'एकूण केंद्रे',
      districts: 'जिल्हे',
      customerSupport: 'ग्राहक सहाय्य',
      searchPlaceholder: 'ग्राहक सेवा केंद्र शोधा...',
      totalCentersInfo: 'महाराष्ट्रातील {count} ग्राहक सेवा केंद्रे',
      noResults: 'कोणतेही सेवा केंद्र सापडले नाही',
      noResultsDesc: 'कृपया आपला शोध बदलून पहा',
      showAllCenters: 'सर्व केंद्रे दाखवा',
      moreInfo: 'अधिक माहिती',
      directions: 'गूगल मॅप',
      contactInfo: 'संपर्क माहिती',
      address: 'पत्ता',
      details: 'तपशील',
      location: 'स्थान',
      quickActions: 'त्वरित क्रिया',
      getDirections: 'मार्ग शोधा',
      additionalHelp: 'अतिरिक्त सहाय्य',
      additionalHelpDesc: 'आजच जवळच्या ग्राहक सेवा केंद्राला भेट द्या. ',
      customerService: 'ग्राहक सेवा',
      customerServiceDesc: 'आमच्या सेवा केंद्रांवर भेट द्या',
      serviceCenters: 'ग्राहक सेवा केंद्रे',
      locationsInMaharashtra: 'महाराष्ट्रातील विविध स्थानांवर',
      visitNearbyCenter: 'जवळच्या',
      customerServiceCenter: 'ग्राहक सेवा केंद्र',
      findNearbyCenter: 'संपर्क साधा',
      personalizedAssistance: 'आमच्या {count} सोयीस्कर स्थानांवर ग्राहक सेवा केंद्रांवर व्यक्तिगत सहाय्य मिळवा',
      thisCenterDedicated: 'हे सेवा केंद्र विविध ग्राहक सेवा पुरविण्यासाठी समर्पित आहे.',
      responseTime: '२४ तासांत'
    }
  };

  // Get current language content
  const currentLang = language;
  const currentContent = translations[currentLang];

  const [centers, setCenters] = useState([]);

  useEffect(() => {
    const loadCenters = async () => {
      try {
        const centersRef = dbRef(database, 'shivpratapmultistate/customer_service_centers');
        const snapshot = await get(centersRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const list = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          }));
          setCenters(list);
        } else {
          setCenters([]);
        }
      } catch (error) {
        console.error('Error loading centers:', error);
      }
    };
    loadCenters();
  }, []);

  // Filter service centers based on search
  const filteredCenters = searchQuery === ''
    ? centers
    : centers.filter(center =>
      (center.name && center.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (center.englishName && center.englishName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (center.address && center.address.toLowerCase().includes(searchQuery.toLowerCase()))
    );

  // Handle location selection
  const handleLocationSelect = (center) => {
    setSelectedLocation(center);
    setTimeout(() => {
      const element = document.getElementById('center-details');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Handle get directions
  const handleGetDirections = (center) => {
    if (center.mapLink) {
      window.open(center.mapLink, '_blank');
      return;
    }
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(center.address)}`;
    window.open(mapsUrl, '_blank');
  };

  return (
    <div className="font-sans bg-gradient-to-b from-rose-50 to-white">
      {/* 1. Hero Banner Section */}
      <section
        className="relative h-[400px] overflow-hidden"
        style={{
          background: 'linear-gradient(rgba(176, 52, 98, 0.9), rgba(220, 53, 69, 0.85))',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0">
          {/* Pattern Overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            backgroundSize: '300px'
          }}></div>
        </div>

        <div className="relative h-full flex flex-col justify-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="flex items-center text-rose-100 text-sm mb-6">
              <a href="/" className="flex items-center hover:text-white transition-colors">
                <FaHome className="mr-2" />
                {currentContent.breadcrumbHome}
              </a>
              <FaChevronRight className="mx-2 opacity-50" />
              <span className="font-semibold text-white">{currentContent.pageTitle}</span>
            </nav>

            {/* Page Heading */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              {currentLang === 'en' ? (
                <>
                  {currentContent.pageTitle.split(' ').map((word, index) => (
                    <span key={index} className="inline-block mr-3 last:mr-0">
                      {word}
                    </span>
                  ))}
                </>
              ) : (
                <>
                  ग्राहक <span className="bg-gradient-to-r from-rose-300 to-pink-300 bg-clip-text text-transparent">सेवा केंद्र</span>
                </>
              )}
            </h1>
            <p className="text-xl md:text-2xl text-rose-100 max-w-3xl mb-8">
              {currentContent.pageSubtitle}
            </p>


          </div>
        </div>
      </section>



      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-3">{currentContent.ourCenters}</h2>
          <p className="text-gray-600">
            {currentContent.ourCentersDesc}
          </p>
        </div>

        {/* Service Centers Listing */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredCenters.map(center => (
            <div
              key={center.id}
              className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:border-rose-400 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
            >
              {/* Center Name - Red Header */}
              <div className="bg-gradient-to-r from-rose-600 to-rose-700 p-4">
                <h3 className="text-lg font-bold text-white mb-1">
                  {currentLang === 'en' ? center.englishName : center.name}
                </h3>
              </div>

              <div className="p-5">
                {/* Address */}
                <div className="mb-5">
                  <div className="flex items-start">
                    <FaMapMarkerAlt className="text-rose-500 mt-1 mr-2.5 flex-shrink-0 text-sm" />
                    <div className="text-gray-600 text-sm leading-relaxed">
                      {currentLang === 'en' && center.englishAddress ? center.englishAddress : center.address}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-5">
                  <p className="text-sm text-gray-500 pl-6">
                    {currentLang === 'en' && center.englishDescription ? center.englishDescription : center.description}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2.5">
                  {/* <button
                    onClick={() => handleLocationSelect(center)}
                    className="flex-1 bg-gradient-to-r from-rose-600 to-rose-800 text-white px-4 py-2 rounded-lg font-medium hover:shadow-lg transition-all duration-300"
                  >
                    {currentContent.moreInfo}
                  </button> */}
                  <button
                    onClick={() => handleGetDirections(center)}
                    className="w-full flex items-center justify-center px-4 py-2.5 rounded-lg border-2 border-rose-600 text-rose-600 font-semibold hover:bg-rose-50 transition-all duration-300"
                  >
                    <FaDirections className="mr-2" />
                    {currentContent.directions}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results Message */}
        {filteredCenters.length === 0 && (
          <div className="text-center py-16 bg-gray-50 rounded-2xl">
            <FaMapMarkerAlt className="text-gray-400 text-5xl mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">{currentContent.noResults}</h3>
            <p className="text-gray-600 mb-6">{currentContent.noResultsDesc}</p>
            <button
              onClick={() => setSearchQuery('')}
              className="bg-gradient-to-r from-rose-600 to-rose-800 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all"
            >
              {currentContent.showAllCenters}
            </button>
          </div>
        )}

        {/* Center Details Section */}
        {selectedLocation && (
          <section id="center-details" className="scroll-mt-24 mt-12">
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              <div className="relative h-48 bg-gradient-to-r from-rose-600 to-rose-800">
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-4xl font-bold text-white">
                        {currentLang === 'en' ? selectedLocation.englishName : selectedLocation.name}
                      </h2>
                    </div>
                    <button
                      onClick={() => setSelectedLocation(null)}
                      className="bg-white text-gray-800 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <FaTimes size={20} />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Contact Details */}
                  <div className="lg:col-span-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">{currentContent.contactInfo}</h3>
                        <div className="space-y-4">
                          <div className="flex items-start">
                            <FaMapMarkerAlt className="text-rose-600 mt-1 mr-4 flex-shrink-0" />
                            <div>
                              <div className="font-bold text-gray-800">{currentContent.address}</div>
                              <div className="text-gray-700">
                                {currentLang === 'en' && selectedLocation.englishAddress ? selectedLocation.englishAddress : selectedLocation.address}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">{currentContent.details}</h3>
                        <div className="text-gray-700 leading-relaxed">
                          <p>
                            {currentLang === 'en' && selectedLocation.englishDescription ? selectedLocation.englishDescription : selectedLocation.description}
                          </p>
                          <div className="mt-6 p-4 bg-rose-50 rounded-lg">
                            <p className="text-sm text-gray-600">
                              {currentContent.thisCenterDedicated}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Map */}
                    <div className="mt-8 pt-8 border-t border-gray-200">
                      <h3 className="text-2xl font-bold text-gray-800 mb-4">{currentContent.location}</h3>
                      <div className="bg-gray-100 rounded-xl h-64 overflow-hidden">
                        <iframe
                          src={`https://maps.google.com/maps?q=${encodeURIComponent(selectedLocation.address)}&output=embed`}
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="Service Center Location Map"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="space-y-4">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">{currentContent.quickActions}</h3>
                    <button
                      onClick={() => handleGetDirections(selectedLocation)}
                      className="w-full bg-gradient-to-r from-rose-600 to-rose-800 text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center"
                    >
                      <FaDirections className="mr-3" />
                      {currentContent.getDirections}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}


      </div>
    </div>
  );
};

export default CustomerServiceCenter;